import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "../../components/live-support/liveSupportComponent.module.scss";
import SupportServices from "./services/SupportService";
import LiveChatForm from "./LiveChatForm";
import LiveChatSection from "./LiveChatSection";
import { getCookie, setCookie } from "../../api/utils/utils";
import { RootState } from "../../redux/store";
import { useSelector } from "react-redux";
import { useAppSelector } from "../../hooks/hook";
import { FaWhatsapp } from "react-icons/fa";
import FooterService from "../../layouts/Footer/footerServices/FooterServices";

const WhatsappIcon = FaWhatsapp as any;

interface Template {
  id: number;
  title: string;
  is_parent: boolean;
  is_file: number;
  title_link?: string | null;
  link_title?: string | null;
  children?: Template[];
  content?: string;
}

interface Message {
  id: number;
  message: string;
  isUser: boolean;
  timestamp: string;
  date?: string;
  isParent?: boolean;
  templateId?: number;
  hasChildren?: boolean;
  isContent?: boolean;
  isLink?: boolean;
  is_file: number;
}

interface ChatItem {
  id: number;
  chat_id: number;
  sender_type: "user" | "admin" | string;
  content: string;
  is_file: number;
  is_read: number;
  hour: string;
}

interface ChatGroup {
  date: string;
  messages: ChatItem[];
}

const LiveSupportComponent = () => {
  const [isLiveSupport, setIsLiveSupport] = useState(false);
  // Chatbot templates (commented out as chatbot is not needed right now)
  // const [chatBotTemplates, setChatBotTemplates] = useState<Template[]>([]);
  // const [childTemplates, setChildTemplates] = useState<Template[]>([]);
  // const [loadingTemplateId, setLoadingTemplateId] = useState<number | null>(null);

  const [isChatSection, setIsChatSection] = useState(false);
  const [isLiveChat, setIsLiveChat] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | number | undefined>(
    () => {
      if (typeof window !== "undefined") {
        const sessionChatId = sessionStorage.getItem("guest-chat-id");
        if (sessionChatId) return sessionChatId;
      }
      return getCookie("guest-chat-id") || undefined;
    }
  );
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  const isBasketOpen = useSelector(
    (state: RootState) => state.basket?.isBasketOpen
  );
  const selectedLanguage = useSelector(
    (state: RootState) => state.language.selectedLanguage?.code
  );
  const translations = useSelector(
    (state: RootState) => state.translations?.data
  );

  useEffect(() => {
    if (isBasketOpen) {
      setIsLiveSupport(false);
    }
  }, [isBasketOpen]);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const reduxWhatsapp = useAppSelector(
    (state: RootState) => state.socialAccounts.whatsapp
  );
  const [whatsappUrl, setWhatsappUrl] = useState<string>("");

  useEffect(() => {
    if (reduxWhatsapp) {
      setWhatsappUrl(reduxWhatsapp);
    }
  }, [reduxWhatsapp]);

  const handleOpenWhatsApp = () => {
    const target = reduxWhatsapp || whatsappUrl;
    if (target) {
      window.open(target, "_blank");
    }
  };

  /* Chatbot templates loading effect (commented out)
  useEffect(() => {
    SupportServices.allChatbotTemplate().then((res) => {
      setChatBotTemplates(res.data.data);
    });
  }, [selectedLanguage]);
  */

  const token = localStorage.getItem("token");

  const formatTimestamp = (item: any): string => {
    if (item?.hour) return item.hour;
    if (item?.created_at) {
      try {
        const d = new Date(item.created_at);
        if (!isNaN(d.getTime())) {
          return d.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      } catch {
        // fallback
      }
      return String(item.created_at).slice(11, 16);
    }
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchLiveMessages = useCallback(async () => {
    setIsLoadingMessages(true);
    try {
      const res = await SupportServices.getMessages();
      const data = res?.data;
      if (!data?.status) return;

      if (data.chat_id) {
        setCookie("guest-chat-id", data.chat_id);
        if (typeof window !== "undefined") {
          sessionStorage.setItem("guest-chat-id", String(data.chat_id));
        }
        setActiveChatId(data.chat_id);
      }

      const groups: ChatGroup[] = data.messages || [];

      const mapped: Message[] = groups.flatMap((group) =>
        (group.messages || []).map((item) => ({
          id: item.id,
          message: item.content ?? "",
          isUser: item.sender_type === "user",
          is_file: Number(item.is_file) || 0,
          timestamp: item.hour || formatTimestamp(item),
          date: group.date,
        }))
      );

      setMessages(mapped);
    } catch (err: any) {
      console.error("Failed to load messages from live-chat/my-messages:", err);
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  const handleGuestRegistered = (newChatId?: number | string) => {
    if (newChatId) {
      setActiveChatId(newChatId);
      setCookie("guest-chat-id", newChatId);
      if (typeof window !== "undefined") {
        sessionStorage.setItem("guest-chat-id", String(newChatId));
      }
    }
    setMessages([]);
    setIsChatSection(true);
    setIsLiveChat(true);
    setLoggedIn(false);
    setIsLoadingMessages(false);
    fetchLiveMessages();
  };

  const handleCloseClick = () => {
    setMessages([]);
    setIsChatSection(false);
    setIsLiveChat(false);
    setIsLiveSupport(false);
  };

  const handleBackToMenu = () => {
    setIsChatSection(false);
    setIsLiveChat(false);
    setLoggedIn(false);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoadingMessages]);

  /* Chatbot navigation & click handlers (commented out)
  const getChildTemplate = (id: number) => {
    SupportServices.childChatbotTemplate(id)
      .then((res) => {
        const children = res.data.data;
        if (children.length > 0) {
          const botMessages = children.map((child: Template) => {
            const isLink = !!(child.title_link && child.link_title);
            return {
              id: child.id,
              message: isLink
                ? `${child.title} <a href="${child.title_link}" target="_blank" rel="noopener noreferrer">${child.link_title}</a>`
                : child.title,
              isUser: false,
              isParent: child.is_parent,
              hasChildren:
                child.is_parent ||
                (child.children && child.children.length > 0),
              templateId: child.id,
              isLink,
              is_file: 0,
              timestamp: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            } as Message;
          });
          setMessages((prev) => [...prev, ...botMessages]);
        }
      })
      .catch((err) => console.log(err));
  };

  const handleTemplateSelect = (template: Template) => {
    const userMessage: Message = {
      id: template.id,
      message: template.title,
      is_file: template.is_file,
      isUser: true,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    setMessages((prev) => [...prev, userMessage]);
    if (template.is_parent) {
      getChildTemplate(template.id);
    }
  };

  const handleMessageClick = (msg: Message) => {
    if (msg.isUser || msg.isContent) return;
    if (msg.templateId) {
      getChildTemplate(msg.templateId);
    }
  };
  */

  const handleStartLiveChat = () => {
    if (token) {
      SupportServices.registerUserForChat()
        .then((res) => {
          if (res?.data?.chat_id) {
            setCookie("guest-chat-id", res.data.chat_id);
            setActiveChatId(res.data.chat_id);
          }
          setIsLiveChat(true);
          setIsChatSection(true);
          setLoggedIn(false);
          fetchLiveMessages();
        })
        .catch(() => {
          setIsLiveChat(true);
          setIsChatSection(true);
          setLoggedIn(false);
          fetchLiveMessages();
        });
      return;
    }

    // Check if guest token already exists in sessionStorage for this session
    const sessionGuestToken =
      typeof window !== "undefined"
        ? sessionStorage.getItem("guest-token-chat")
        : null;

    if (sessionGuestToken) {
      // Session has valid guest token: continue chat without asking for form again
      setIsLiveChat(true);
      setIsChatSection(true);
      setLoggedIn(false);
      fetchLiveMessages();
      return;
    }

    // No session token yet: ask guest to fill the start form
    setIsChatSection(true);
    setLoggedIn(true);
    setIsLiveChat(false);
    setMessages([]);
  };

  if (isBasketOpen) {
    return null;
  }

  return (
    <div style={{ width: "100%" }}>
      {!isLiveSupport && (
        <div className={styles.floatingButtons}>
          <button
            type="button"
            onClick={() => setIsLiveSupport(true)}
            className={styles.unifiedSupportBtn}
            aria-label={translations?.contact || "Dəstək"}
            title={translations?.contact || "Dəstək"}
            tabIndex={0}
          >
            <div className={styles.btnIconWrap}>
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span
                className={styles.wpBadgeDot}
                title="WhatsApp & Canlı Dəstək"
              />
            </div>
            <span className={styles.btnText}>
              {translations?.contact || "Dəstək"}
            </span>
          </button>
        </div>
      )}

      {isLiveSupport && (
        <div className={styles.liveSupportWrapper}>
          <div
            className={`${styles.liveSupport} ${
              isChatSection ? styles.liveSupportChatActive : ""
            }`}
          >
            {/* Header: Menu Header or Live Chat Header */}
            {!isChatSection ? (
              <div className={styles.supportCardHeader}>
                <div className={styles.supportCardHeaderLeft}>
                  <h3 className={styles.supportCardTitle}>
                    {translations?.contact || "Dəstək Xidməti"}
                  </h3>
                  <p className={styles.supportCardSubtitle}>
                    Bizimlə əlaqə saxlamaq üçün uyğun üsulu seçin
                  </p>
                </div>
                <button
                  className={styles.closeButton}
                  onClick={handleCloseClick}
                  aria-label="Bağla"
                  tabIndex={0}
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className={styles.liveChatModernHeader}>
                <button
                  type="button"
                  className={styles.headerBackBtn}
                  onClick={handleBackToMenu}
                  aria-label="Geri"
                  title="Geri"
                  tabIndex={0}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>

                <div className={styles.headerAgentInfo}>
                  <div className={styles.headerAvatarWrap}>
                    <div className={styles.headerAvatar}>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                        <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                      </svg>
                    </div>
                    <span className={styles.headerOnlineBadge} />
                  </div>
                  <div className={styles.headerTextWrap}>
                    <span className={styles.headerAgentTitle}>
                      {translations?.live_chat || "Canlı Dəstək"}
                    </span>
                    
                  </div>
                </div>

                <button
                  className={styles.closeButton}
                  onClick={handleCloseClick}
                  aria-label="Bağla"
                  tabIndex={0}
                >
                  ✕
                </button>
              </div>
            )}

            {/* Support Options Menu */}
            {!isChatSection && (
              <div className={styles.liveSupportBody}>
                <div className={styles.unifiedOptions}>
                  {whatsappUrl && (
                    <button
                      type="button"
                      className={styles.optionWhatsappBtn}
                      onClick={handleOpenWhatsApp}
                      tabIndex={0}
                    >
                      <div className={styles.optionIconBoxWp}>
                        <WhatsappIcon size={24} />
                      </div>
                      <div className={styles.optionTextBox}>
                        <span className={styles.optionTitle}>WhatsApp</span>
                        <span className={styles.optionDesc}>
                          WhatsApp ilə dərhal yazın
                        </span>
                      </div>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={styles.arrowIcon}
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  )}

                  <button
                    type="button"
                    disabled={isLiveChat}
                    className={styles.optionLiveChatBtn}
                    onClick={handleStartLiveChat}
                    tabIndex={0}
                  >
                    <div className={styles.optionIconBoxLive}>
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    </div>
                    <div className={styles.optionTextBox}>
                      <span className={styles.optionTitle}>
                        {translations?.live_chat || "Canlı Dəstək"}
                      </span>
                      <span className={styles.optionDesc}>
                        Operatorla saytda canlı söhbət
                      </span>
                    </div>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={styles.arrowIcon}
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Guest Registration Form */}
            {isChatSection && loggedIn && !token && (
              <div className={styles.liveSupportBody}>
                <LiveChatForm
                  setLoggedIn={setLoggedIn}
                  setIsLiveChat={setIsLiveChat}
                  onSuccess={handleGuestRegistered}
                  onBack={handleBackToMenu}
                />
              </div>
            )}

            {/* Live Chat Message Area and Input Section */}
            {isChatSection && (!loggedIn || token) && (
              <div className={styles.chatContainerWrapper}>
                <div className={styles.chatMessagesArea}>
                  {isLoadingMessages && (
                    <div className={styles.loadingSkeletonContainer}>
                      <div className={styles.skeletonBubbleSupport} />
                      <div className={styles.skeletonBubbleUser} />
                      <div className={styles.skeletonBubbleSupport} />
                    </div>
                  )}

                  {messages?.map((msg, idx) => {
                    if (
                      msg.message === translations?.yess ||
                      msg.message === translations?.nope ||
                      msg.message === translations?.end_chat
                    ) {
                      return null;
                    }

                    const prevMsg = idx > 0 ? messages[idx - 1] : null;
                    const isNewDate = !!msg.date && (!prevMsg || prevMsg.date !== msg.date);

                    return (
                      <React.Fragment key={idx}>
                        {isNewDate && (
                          <div className={styles.chatDateDivider}>
                            <span>{msg.date}</span>
                          </div>
                        )}
                        <div
                          className={
                            msg.isUser
                              ? styles.userMessageRow
                              : styles.supportMessageRow
                          }
                        >
                        {!msg.isUser && (
                          <div
                            className={styles.operatorMiniAvatar}
                            title="Dəstək"
                          >
                            <svg
                              width="13"
                              height="13"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                            </svg>
                          </div>
                        )}

                        <div
                          className={
                            msg.isUser
                              ? styles.userBubble
                              : msg.isContent
                              ? styles.contentBubble
                              : styles.supportBubble
                          }
                        >
                          {Number(msg.is_file) === 1 ? (
                            <div className={styles.fileMessageContent}>
                              {msg.message.match(
                                /\.(jpeg|jpg|gif|png|webp)$/i
                              ) ? (
                                <a
                                  href={msg.message}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.imageLinkWrap}
                                >
                                  <img
                                    src={msg.message}
                                    alt="attachment"
                                    className={styles.messageImage}
                                  />
                                </a>
                              ) : (
                                <a
                                  href={msg.message}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.fileDownloadCard}
                                >
                                  <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="12" y1="18" x2="12" y2="12" />
                                    <line x1="9" y1="15" x2="15" y2="15" />
                                  </svg>
                                  <span className={styles.fileName}>
                                    {msg.message.split("/").pop()}
                                  </span>
                                </a>
                              )}
                            </div>
                          ) : msg.isLink ? (
                            <div
                              className={styles.linkMessageText}
                              dangerouslySetInnerHTML={{
                                __html: msg.message,
                              }}
                            />
                          ) : (
                            <div className={styles.textMessageContent}>
                              {msg.message}
                            </div>
                          )}

                          <div className={styles.bubbleFooter}>
                            <span className={styles.bubbleTimestamp}>
                              {msg.timestamp}
                            </span>
                            {msg.isUser && (
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className={styles.checkIcon}
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {isLiveChat && (
                  <LiveChatSection
                    setMessages={setMessages}
                    chatId={activeChatId}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveSupportComponent;
